import { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { apiPost } from "../api/http";
import { useAuth } from "../context/AuthContext";
import { parseApiError } from "../utils/apiErrors";
import { formatOtpError, formatOtpExpiryHint } from "../utils/otpErrors";
import OtpInput from "../components/OtpInput";

const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID;

const GoogleIcon = () => (
  <svg viewBox="0 0 24 24" width="18" height="18">
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"/>
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18A10.96 10.96 0 0 0 1 12c0 1.77.42 3.45 1.18 4.93l3.66-2.84z"/>
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
  </svg>
);

// ─── View constants ─────────────────────────────────────────────────────────
const VIEW_DEFAULT    = "default";      // email input + Google button
const VIEW_OTP        = "otp";          // OTP verification step
const VIEW_LINK       = "link";         // Google account linking confirmation
const VIEW_REGISTER   = "register";     // Google new-user registration + ToS

const Auth = () => {
  const navigate = useNavigate();
  const location = useLocation();

  if (process.env.REACT_APP_STAGE === "alpha") {
    return (
      <div className="page auth-container" style={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "70vh" }}>
        <div className="card" style={{
          maxWidth: "420px",
          textAlign: "center",
          padding: "48px 32px",
          borderRadius: "16px",
          border: "1px solid rgba(255, 255, 255, 0.1)",
          boxShadow: "0 20px 40px -15px rgba(0, 0, 0, 0.3)"
        }}>
          <div style={{
            background: "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            width: "72px",
            height: "72px",
            borderRadius: "50%",
            fontSize: "2.2rem",
            marginBottom: "24px",
            color: "#fff",
            boxShadow: "0 8px 20px -6px rgba(217, 119, 6, 0.5)"
          }}>
            🚧
          </div>
          <h1 className="page-title" style={{ fontSize: "1.8rem", fontWeight: "700", marginBottom: "12px" }}>
            Alpha Mode Active
          </h1>
          <p style={{ color: "var(--text-muted)", marginBottom: "32px", fontSize: "0.95rem", lineHeight: "1.6" }}>
            Login and registration are currently disabled during the Alpha stage. Please use the free quick audit directly from the home page.
          </p>
          <button
            onClick={() => navigate("/audit")}
            className="btn btn-primary"
            style={{ width: "100%", padding: "12px", fontSize: "1.05rem", fontWeight: "600" }}
          >
            Go to Free Audit
          </button>
        </div>
      </div>
    );
  }

  const { login } = useAuth();

  // ─── Common state ─────────────────────────────────────────────────────────
  const [view, setView]           = useState(VIEW_DEFAULT);
  const [email, setEmail]         = useState("");
  const [loading, setLoading]     = useState(false);
  const [error, setError]         = useState("");
  const [message, setMessage]     = useState("");

  // ─── OTP state ────────────────────────────────────────────────────────────
  const [otp, setOtp]                     = useState("");
  const [otpSessionId, setOtpSessionId]   = useState("");
  const [otpExpiry, setOtpExpiry]         = useState(null);
  const [resendAvailableAt, setResendAvailableAt] = useState(null);
  const [resendTimer, setResendTimer]     = useState(0);
  const [resendLoading, setResendLoading] = useState(false);

  // ─── Google linking / registration state ─────────────────────────────────
  // Holds the raw Google credential (ID token) across the multi-step Google flow.
  const [googleCredential, setGoogleCredential]   = useState(null);
  const [googleProfile, setGoogleProfile]         = useState(null); // { email, name, avatarUrl }
  const [acceptedTerms, setAcceptedTerms]         = useState(false);

  const otpExpiryHint = useMemo(() => formatOtpExpiryHint(otpExpiry), [otpExpiry, resendTimer]);

  const handleLogin = useCallback((token, expiresAt = null, refreshToken = null, isNewUser = false) => {
    login(token, expiresAt, refreshToken, isNewUser);
    const origin = location.state?.from?.pathname || "/dashboard";
    navigate(origin, { replace: true });
  }, [navigate, login, location]);

  const setOtpSessionDetails = useCallback((sessionId, expiry, cooldownSeconds = 30) => {
    setView(VIEW_OTP);
    setOtpSessionId(sessionId || "");
    setOtpExpiry(expiry ? Number(expiry) : null);
    setResendAvailableAt(Date.now() + cooldownSeconds * 1000);
  }, []);

  // ─── Google callback (called by Google's SDK) ─────────────────────────────
  const handleGoogleResponse = useCallback(async (response) => {
    setError("");
    setLoading(true);
    try {
      const { data } = await apiPost("/api/auth/google-login", {
        credential: response.credential,
      });

      if (data.status === "LINK_REQUIRED") {
        // Existing email account found — prompt user to link Google
        setGoogleCredential(response.credential);
        setGoogleProfile({ email: data.email, name: data.name, avatarUrl: data.avatarUrl });
        setEmail(data.email || "");
        setView(VIEW_LINK);
        return;
      }

      if (data.status === "REGISTRATION_REQUIRED") {
        // New user — prompt to complete registration + accept ToS
        setGoogleCredential(response.credential);
        setGoogleProfile({ email: data.email, name: data.name, avatarUrl: data.avatarUrl });
        setEmail(data.email || "");
        setView(VIEW_REGISTER);
        return;
      }

      // Already linked — signed in immediately
      handleLogin(data.token, data.expiresAt, data.refreshToken, data.isNewUser);
    } catch (err) {
      setError(parseApiError(err, "Google sign-in failed"));
    } finally {
      setLoading(false);
    }
  }, [handleLogin]);

  useEffect(() => {
    if (!GOOGLE_CLIENT_ID || !window.google?.accounts) return;
    window.google.accounts.id.initialize({
      client_id: GOOGLE_CLIENT_ID,
      callback: handleGoogleResponse,
    });
    window.google.accounts.id.renderButton(
      document.getElementById("google-signin-btn"),
      { theme: "outline", size: "large", width: 320, text: "continue_with" }
    );
  }, [handleGoogleResponse]);

  // ─── Email / OTP flow ─────────────────────────────────────────────────────
  const handleEmailLogin = async (e) => {
    e.preventDefault();
    if (!email) return;
    if (view === VIEW_OTP && otp.trim().length !== 6) {
      setError("Enter all 6 digits.");
      return;
    }
    setLoading(true);
    setError("");
    setMessage("");
    try {
      const { data } = await apiPost("/api/auth/email-login", {
        email,
        otp: view === VIEW_OTP ? otp.trim() : undefined,
        otpSessionId: view === VIEW_OTP ? otpSessionId : undefined,
      });
      if (data?.requiresOtp) {
        setOtpSessionDetails(data.otpSessionId, data.clientExpiry || data.expiry, 30);
        return;
      }
      handleLogin(data.token, data.expiresAt, data.refreshToken, data.isNewUser);
    } catch (err) {
      setError(formatOtpError(parseApiError(err, "Sign-in failed")));
    } finally {
      setLoading(false);
    }
  };

  const handleResendOtp = useCallback(async () => {
    if (!email || resendTimer > 0) return;
    setError("");
    setMessage("");
    setResendLoading(true);
    try {
      const { data } = await apiPost("/api/auth/request-otp", { email });
      if (data?.otpSessionId) {
        setOtpSessionDetails(data.otpSessionId, data.clientExpiry || data.expiry, 30);
      }
    } catch (err) {
      setError(formatOtpError(parseApiError(err, "Failed to resend OTP")));
    } finally {
      setResendLoading(false);
    }
  }, [email, resendTimer, setOtpSessionDetails]);

  // ─── Google Link confirmation ─────────────────────────────────────────────
  const handleConfirmLink = async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await apiPost("/api/auth/link-google", {
        credential: googleCredential,
      });
      handleLogin(data.token, data.expiresAt, data.refreshToken, false);
    } catch (err) {
      setError(parseApiError(err, "Account linking failed"));
    } finally {
      setLoading(false);
    }
  };

  // ─── Google Registration ──────────────────────────────────────────────────
  const handleCompleteRegistration = async (e) => {
    e.preventDefault();
    if (!acceptedTerms) {
      setError("You must accept the terms of service to continue.");
      return;
    }
    setLoading(true);
    setError("");
    try {
      const { data } = await apiPost("/api/auth/register-google", {
        credential: googleCredential,
        acceptedTerms: true,
      });
      handleLogin(data.token, data.expiresAt, data.refreshToken, true);
    } catch (err) {
      setError(parseApiError(err, "Registration failed"));
    } finally {
      setLoading(false);
    }
  };

  const handleBackToDefault = () => {
    setView(VIEW_DEFAULT);
    setOtp("");
    setOtpSessionId("");
    setOtpExpiry(null);
    setResendAvailableAt(null);
    setResendTimer(0);
    setGoogleCredential(null);
    setGoogleProfile(null);
    setAcceptedTerms(false);
    setError("");
    setMessage("");
  };

  const handleDevLogin = async (role = "user") => {
    setLoading(true);
    setError("");
    try {
      const { data } = await apiPost("/api/auth/dev-login", { role });
      handleLogin(data.token, data.expiresAt, data.refreshToken, false);
    } catch (err) {
      setError(parseApiError(err, `Dev ${role} login failed`));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!resendAvailableAt) {
      setResendTimer(0);
      return;
    }
    const interval = setInterval(() => {
      const remaining = Math.max(0, Math.ceil((resendAvailableAt - Date.now()) / 1000));
      setResendTimer(remaining);
      if (remaining <= 0) clearInterval(interval);
    }, 250);
    return () => clearInterval(interval);
  }, [resendAvailableAt]);

  const searchParams = new URLSearchParams(location.search);
  const mode = searchParams.get("mode");

  const resendDisabled = loading || resendLoading || !email || resendTimer > 0;

  // =========================================================================
  // RENDER: LINK_REQUIRED view
  // =========================================================================
  if (view === VIEW_LINK) {
    return (
      <div className="auth-shell">
        <div className="auth-container">
          <div className="auth-card card">
            <div className="auth-brand">
              <img src="/logo.svg" alt="Nova SEO Logo" style={{ height: "44px", width: "44px", borderRadius: "12px", objectFit: "contain" }} />
              <div>
                <div className="auth-brand-title">Nova SEO</div>
                <div className="auth-brand-subtitle">Link your Google account</div>
              </div>
            </div>

            <h1 className="auth-title">Connect Google to your account</h1>
            <p className="auth-subtitle">
              An account already exists for <strong>{googleProfile?.email}</strong> using email sign-in.
              Link your Google account so you can sign in with either method.
            </p>

            {googleProfile?.avatarUrl && (
              <div style={{ display: "flex", justifyContent: "center", marginBottom: "16px" }}>
                <img
                  src={googleProfile.avatarUrl}
                  alt={googleProfile.name || "Google profile"}
                  style={{ width: "56px", height: "56px", borderRadius: "50%", border: "2px solid var(--border-color)" }}
                />
              </div>
            )}

            {error ? <div className="progress-error" style={{ marginBottom: 16 }}>{error}</div> : null}

            <button
              id="auth-confirm-link-btn"
              className="btn btn-primary auth-submit-btn input-touch"
              onClick={handleConfirmLink}
              disabled={loading}
              style={{ marginBottom: "12px" }}
            >
              {loading ? "Linking account..." : "Yes, link my Google account"}
            </button>

            <button type="button" className="auth-link-btn" onClick={handleBackToDefault}>
              Cancel — go back
            </button>

            <p className="auth-footnote" style={{ marginTop: "16px" }}>
              By linking, you'll be able to sign in with Google or your email OTP going forward.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // =========================================================================
  // RENDER: REGISTRATION_REQUIRED view
  // =========================================================================
  if (view === VIEW_REGISTER) {
    return (
      <div className="auth-shell">
        <div className="auth-container">
          <div className="auth-card card">
            <div className="auth-brand">
              <img src="/logo.svg" alt="Nova SEO Logo" style={{ height: "44px", width: "44px", borderRadius: "12px", objectFit: "contain" }} />
              <div>
                <div className="auth-brand-title">Nova SEO</div>
                <div className="auth-brand-subtitle">Complete your registration</div>
              </div>
            </div>

            <h1 className="auth-title">Create your account</h1>
            <p className="auth-subtitle">
              We'll create a new Nova SEO account for <strong>{googleProfile?.email}</strong> using your Google profile.
            </p>

            {googleProfile?.avatarUrl && (
              <div style={{ display: "flex", alignItems: "center", gap: "12px", background: "var(--surface-2, rgba(255,255,255,0.04))", borderRadius: "10px", padding: "12px 16px", marginBottom: "20px" }}>
                <img
                  src={googleProfile.avatarUrl}
                  alt={googleProfile.name || "Google profile"}
                  style={{ width: "44px", height: "44px", borderRadius: "50%", border: "2px solid var(--border-color)" }}
                />
                <div>
                  <div style={{ fontWeight: 600, fontSize: "0.95rem" }}>{googleProfile.name}</div>
                  <div style={{ color: "var(--text-muted)", fontSize: "0.85rem" }}>{googleProfile.email}</div>
                </div>
              </div>
            )}

            {error ? <div className="progress-error" style={{ marginBottom: 16 }}>{error}</div> : null}

            <form onSubmit={handleCompleteRegistration} className="auth-form">
              <label style={{ display: "flex", alignItems: "flex-start", gap: "10px", cursor: "pointer", marginBottom: "20px", fontSize: "0.9rem", color: "var(--text-muted)", lineHeight: "1.5" }}>
                <input
                  id="auth-register-terms-checkbox"
                  type="checkbox"
                  checked={acceptedTerms}
                  onChange={(e) => setAcceptedTerms(e.target.checked)}
                  style={{ marginTop: "2px", flexShrink: 0, accentColor: "var(--primary)" }}
                />
                I agree to the{" "}
                <a href="/terms" target="_blank" rel="noopener noreferrer" style={{ color: "var(--primary)" }}>
                  Terms of Service
                </a>{" "}
                and{" "}
                <a href="/privacy" target="_blank" rel="noopener noreferrer" style={{ color: "var(--primary)" }}>
                  Privacy Policy
                </a>
              </label>

              <button
                id="auth-complete-registration-btn"
                className="btn btn-primary auth-submit-btn input-touch"
                type="submit"
                disabled={loading || !acceptedTerms}
              >
                {loading ? "Creating your account..." : "Complete Registration"}
              </button>
            </form>

            <button type="button" className="auth-link-btn" onClick={handleBackToDefault} style={{ marginTop: "12px" }}>
              Cancel — go back
            </button>
          </div>
        </div>
      </div>
    );
  }

  // =========================================================================
  // RENDER: Default (email + Google) and OTP view
  // =========================================================================
  let headingText = "Sign in or create account";
  if (view === VIEW_OTP) {
    headingText = "Verify Email OTP";
  } else if (mode === "signup") {
    headingText = "Create your account";
  } else if (mode === "signin") {
    headingText = "Welcome back";
  }

  return (
    <div className="auth-shell">
      <div className="auth-container">
        <div className="auth-card card">
          <div className="auth-brand">
            <img src="/logo.svg" alt="Nova SEO Logo" style={{ height: "44px", width: "44px", borderRadius: "12px", objectFit: "contain" }} />
            <div>
              <div className="auth-brand-title">Nova SEO</div>
              <div className="auth-brand-subtitle">Secure passwordless access</div>
            </div>
          </div>

          <h1 className="auth-title">{headingText}</h1>
          {view !== VIEW_OTP ? (
            <p className="auth-subtitle">
              New here? We'll create your account automatically after email verification.
            </p>
          ) : (
            <p className="auth-subtitle">
              We sent a 6-digit code to <strong>{email}</strong>
            </p>
          )}

          {view !== VIEW_OTP ? (
            <>
              {GOOGLE_CLIENT_ID ? (
                <div className="auth-google-wrap">
                  <div id="google-signin-btn" />
                </div>
              ) : (
                <button className="btn btn-google" type="button" disabled>
                  <GoogleIcon /> Continue with Google
                </button>
              )}
              <div className="divider">or continue with email</div>
            </>
          ) : null}

          <form className="auth-form" onSubmit={handleEmailLogin}>
            <label className="field-label">
              Email address
              <input
                className="input input-touch"
                type="email"
                placeholder="you@company.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                readOnly={view === VIEW_OTP}
                required
              />
            </label>
            {view === VIEW_OTP ? (
              <div className="field-label">
                Verification code
                <OtpInput value={otp} onChange={setOtp} disabled={loading} autoFocus />
                {otpExpiryHint ? <span className="otp-expiry-hint">{otpExpiryHint}</span> : null}
              </div>
            ) : null}
            <button className="btn btn-primary auth-submit-btn input-touch" type="submit" disabled={loading || !email}>
              {loading ? "Please wait..." : view === VIEW_OTP ? "Verify and continue" : "Continue with Email"}
            </button>
            {view === VIEW_OTP ? (
              <div className="auth-actions">
                <button type="button" className="auth-link-btn" onClick={handleBackToDefault}>
                  Change email
                </button>
                <button
                  type="button"
                  className={`auth-link-btn ${resendDisabled ? "is-disabled" : ""}`}
                  onClick={handleResendOtp}
                  disabled={resendDisabled}
                  aria-disabled={resendDisabled}
                >
                  {resendLoading
                    ? "Resending..."
                    : resendTimer > 0
                      ? `Resend in ${resendTimer}s`
                      : "Resend code"}
                </button>
              </div>
            ) : null}
          </form>

          <p className="auth-footnote">
            {view === VIEW_OTP
              ? "Check Promotions or Spam if the code doesn't arrive within a minute."
              : "No password needed. We'll email you a one-time code to sign in securely."}
          </p>

          {message ? <div className="progress-info" style={{ marginTop: 16 }}>{message}</div> : null}
          {error ? <div className="progress-error">{error}</div> : null}

          {process.env.NODE_ENV !== "production" ? (
            <div className="dev-box" style={{ display: "flex", flexDirection: "column", gap: "8px", marginTop: "16px", padding: "12px", border: "1px dashed var(--warning)", borderRadius: "8px" }}>
              <div className="dev-box-label" style={{ fontWeight: "bold", fontSize: "0.85rem", color: "var(--warning)", marginBottom: "4px" }}>Dev Quick Login</div>
              <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
                <button className="btn btn-warning btn-sm" type="button" onClick={() => handleDevLogin("admin")} disabled={loading}>
                  Admin
                </button>
                <button className="btn btn-warning btn-sm" type="button" onClick={() => handleDevLogin("owner")} disabled={loading}>
                  Workspace Owner
                </button>
                <button className="btn btn-warning btn-sm" type="button" onClick={() => handleDevLogin("member")} disabled={loading}>
                  Workspace Member
                </button>
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default Auth;
